/* global UpNextStreamOverrideEnabled, UpNextToastEnabled */
import React, { Component } from 'react';
import ReactTimeout from 'react-timeout';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import Loader from 'components/Loader';
import { getCookie } from 'helpers/Storage';
import { noop } from 'helpers/GlobalHelper';
import { v4 as uuidv4 } from 'uuid';
import { UpNextToast, VideoDetail } from 'components/VideoPlayerGlobal/components';
import {
  getLiveVideo,
  getVideoDetailProps,
  isVideoLive,
} from 'components/VideoPlayerGlobal/Helper';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
} from 'helpers/BreakpointHelper';
import {
  LIVE_EPG_THUMBNAIL_IMAGE,
  LIVE_PLAYER_BACKGROUND_IMAGE,
  getDynamicImage,
  renderImage,
} from 'helpers/ImageHelper';
import { geolocationEnabled, isEpgv2Enabled } from 'helpers/ConfigHelper';
import { RESTART_TYPE_LIVE } from 'screens/Watch/WatchHelpers';
import {
  DEBUG_PANEL_TIMEOUT_DISABLED,
  FOX,
  getAdBlockDetectCookie,
  getAuthZEntitlementIssues,
  getOomLivePlayerEnabled,
  isSportsContent,
  OOM_LIVE_PLAYER_IMAGE_COOKIE_NAME,
  OOM_LIVE_PLAYER_URL_COOKIE_NAME,
  removeAdBlockDetectCookie,
  setAdBlockDetectCookie,
} from 'helpers/ContentHelper';
import {
  now,
  stringToDate,
  isBefore,
  subMinutes,
  differenceInMilliseconds,
} from 'helpers/DateTimeHelper';
import { getLocation as getGeoLocation } from 'helpers/GeoLocationHelper';
import { logPlayerError, logPlayerEvent } from 'helpers/LogReportHelpers';
import {
  LIVE,
  INTERRUPTION_METHODS,
  PLAYER_CONTEXT_LIVE,
  getActivePlayer,
  getPlayerState,
  isPlayerTypeHero,
  playerHideControls,
  playerPause,
  playerPlay,
  playerSetFullscreen,
  setStillWatchingHideTimeout,
  setStillWatchingShowTimeout,
  stillWatchingHideCb,
  stillWatchingShowCb,
} from 'helpers/PlayerHelper';
import {
  getLocation,
  helperGetHomeUrl,
  helperGetRelativeUrl,
  helperGoToRelativeUrl,
  getUrlParams,
} from 'helpers/RouteHelper';
import {
  callSegmentPlayerEvent,
  getSegmentLiveViewedVideo,
  getSegmentPodId,
  getSegmentReferrer,
  getSegmentSourceName,
  getSegmentSourceType,
  PageNames,
  SEGMENT_CONTENT_PLAYING_INTERVAL_SEC,
  SegmentPlayerStates,
  SegmentVideoEvents,
  setSegmentContentPersonality,
  trackPageReferrerAnalytics,
  updateNielsenOTTStatus,
} from '@foxcorp/delta-web-analytics';

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    renderInfoInDetail: false,
    defaultHideAltHeader: false,
    stillWatchingTimeoutEnabled: false,
    showMobileControls: false,
    upNextToastHidesVideoDetail: true,
    playerMetadataOverlay: true,
    fncHideVideoDetails: false,
  },
  [SITE_PLATFORM_WEB]: {
    renderInfoInDetail: false,
    defaultHideAltHeader: false,
    stillWatchingTimeoutEnabled: false,
    showMobileControls: false,
    upNextToastHidesVideoDetail: true,
    playerMetadataOverlay: true,
    fncHideVideoDetails: false,
  },
  [SITE_PLATFORM_TABLET]: {
    renderInfoInDetail: true,
    defaultHideAltHeader: true,
    stillWatchingTimeoutEnabled: false,
    showMobileControls: true,
    upNextToastHidesVideoDetail: false,
    playerMetadataOverlay: false,
    fncHideVideoDetails: true,
  },
  [SITE_PLATFORM_MOBILE]: {
    renderInfoInDetail: true,
    defaultHideAltHeader: true,
    stillWatchingTimeoutEnabled: false,
    showMobileControls: true,
    upNextToastHidesVideoDetail: false,
    playerMetadataOverlay: false,
    fncHideVideoDetails: true,
  },
};

class LiveContext extends Component {
  constructor(props) {
    super(props);
    const geoLocationDeclined = !props.coordinates;
    this.state = {
      contentStarted: false,
      currentVideo: null,
      geoLocationDeclined,
      geoLocationTimeoutFail: false,
      geoLocationLoading: null,
      secondsViewed: 0,
      showStillWatching: false,
      // trackedAnalyticsReferrer is used in trackPageReferrerAnalytics
      // eslint-disable-next-line react/no-unused-state
      trackedAnalyticsReferrer: false,
      showUpNextToast: false,
      playerError: null,
    };
    this.setStillWatchingHideTimeout = setStillWatchingHideTimeout.bind(this);
    this.setStillWatchingShowTimeout = setStillWatchingShowTimeout.bind(this);
    this.stillWatchingHideCb = stillWatchingHideCb.bind(this);
    this.stillWatchingShowCb = stillWatchingShowCb.bind(this);
    this.trackPageReferrerAnalytics = trackPageReferrerAnalytics.bind(this);
    this.eventVideoProps = null;
    this.playbackProps = null;
    this.intervalFetchUpNext = null;
    this.showUpNextTimeout = null;
    this.hideUpNextTimeout = null;
    this.exitStreamTimeout = null;
    this.AD_INTERVAL_SEC = 1;
  }

  componentDidMount() {
    const { setShouldResetStillWatching, shouldResetStillWatching } = this.props;
    if (shouldResetStillWatching) {
      this.resetStillWatchingTimeout();
      setShouldResetStillWatching(false);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { nextVideos, nextVideosValidFor, panelVideo } = this.props;
    const {
      nextVideos: newNextVideos,
      nextVideosValidFor: newNextVideosValidFor,
      panelVideo: nextPanelVideo,
    } = nextProps;

    if (nextVideosValidFor !== newNextVideosValidFor && newNextVideosValidFor) {
      this.setIntervalFetchUpNext(newNextVideosValidFor);
    }

    if (nextVideos !== newNextVideos && newNextVideos) {
      this.setShowUpNextTimeout(newNextVideos);
    }

    if (panelVideo && panelVideo !== nextPanelVideo) {
      this.setState({ currentVideo: null });
    }
  }

  componentDidUpdate(prevProps) {
    const { setShouldResetStillWatching, shouldResetStillWatching } = this.props;
    const { shouldResetStillWatching: prevShouldResetStillWatching } = prevProps;
    if (shouldResetStillWatching && !prevShouldResetStillWatching) {
      this.resetStillWatchingTimeout();
      setShouldResetStillWatching(false);
    }
  }

  componentWillUnmount() {
    const currentPlayerState = getPlayerState(getActivePlayer());
    if (currentPlayerState === 'PLAYING' || currentPlayerState === 'AD') {
      callSegmentPlayerEvent({
        eventName: SegmentVideoEvents.videoPlaybackInterrupted,
        props: {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          method: INTERRUPTION_METHODS.BROWSER_ACTION,
          video_player_state: SegmentPlayerStates.PAUSE,
          session_id: this.sessionId ?? '',
        },
      });
    }
  }

  onExitEventStream = e => {
    const { setTimeout, setStreamExit, errors } = this.props;
    const timeout = _.get(e, 'exitEventStreamSlateDuration', 30);
    this.exitStreamTimeout = setTimeout(() => {
      if (errors?.[0]?.entitlementName) return null;
      return setStreamExit(true);
    }, timeout * 1000);
  };

  onHideUpNextToast = () => {
    const { clearTimeout } = this.props;
    clearTimeout(this.hideUpNextTimeout);
    this.setState({ showUpNextToast: false });
  };

  onStillWatchingClose = () => {
    const { clearTimeout, playerReference, stillWatchingHideTimeout } = this.props;
    clearTimeout(stillWatchingHideTimeout);
    this.resetStillWatchingTimeout();
    this.setState({ showStillWatching: false });
    playerPlay(playerReference);
  };

  onWatchUpNextToast = () => {
    const { clearTimeout, nextVideos, setPlayerState, setVideoPanel } = this.props;
    clearTimeout(this.hideUpNextTimeout);
    const nextVideo = _.get(nextVideos, '1');
    const nextVideoLivePlayerScreenUrl = _.get(nextVideo, 'livePlayerScreenUrl');
    if (!nextVideoLivePlayerScreenUrl) {
      this.setState({ showUpNextToast: false });
      return;
    }
    this.resetStillWatchingTimeout();
    this.setState({ showUpNextToast: false });
    setPlayerState({
      mediaStarted: false,
      isAutoPlay: false,
      firstFramePlayed: false,
    });
    setVideoPanel({ video: nextVideo, source: PLAYER_CONTEXT_LIVE });
  };

  getLiveVideoData() {
    const { fetchedVideo, nextVideos } = this.props;
    if (_.isEmpty(nextVideos) && isVideoLive(fetchedVideo)) {
      return fetchedVideo;
    }
    return getLiveVideo({ nextVideos });
  }

  getPlayerEvents = () => {
    const {
      defaultPlayerEvents,
      fetchedVideo,
      floodlightEvent,
      playerReference,
      saveLiveRestart,
      setPlayerState,
      setVideoStartedPlayback,
    } = this.props;

    const callSign = _.get(fetchedVideo, 'callSign');

    return {
      ...defaultPlayerEvents,
      onReady: contentVideoData => {
        const { video } = this.props;
        const categoryTags = _.get(video, 'categoryTags', false);
        if (categoryTags) {
          setSegmentContentPersonality(categoryTags);
        }
        this.videoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.contentProps = _.get(contentVideoData, 'trackingData.content', {});
        this.sessionId = uuidv4();
      },
      onAdPodStart: contentVideoData => {
        console.debug('!!live - onAdPodStart');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const data = {
          ...this.videoProps,
          ...this.adData,
          session_id: this.sessionId ?? '',
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdPodStarted,
          props: data,
        });
        setPlayerState({ adPlaying: true });
      },
      onAdPodComplete: contentVideoData => {
        console.debug('!!live - onAdPodComplete');
        setPlayerState({ adPlaying: false });
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const data = {
          ...this.videoProps,
          ...this.adData,
          session_id: this.sessionId ?? '',
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdPodCompleted,
          props: data,
        });
      },
      onFirstFrame: async () => {
        console.debug('!!live - onFirstFrame');
        setVideoStartedPlayback(true);
        const {
          account: currentAccount,
          anonymousAccount: currentAnonymousAccount,
          anonymousId: currentAnonymousId,
          anonymousLogin: currentAnonymousLogin,
          segmentReferrer: prevSegmentReferrer,
        } = this.props;
        const loginAnonymousUser =
          !currentAccount &&
          !currentAnonymousAccount &&
          currentAnonymousId &&
          currentAnonymousLogin;
        if (loginAnonymousUser) {
          await currentAnonymousLogin({ deviceId: currentAnonymousId });
        }
        const { segmentSourceName, segmentSourceType } = this.getSegmentSourceData();
        const segmentReferrer = getSegmentReferrer({
          segmentSourceType,
          prevSegmentReferrer,
        });
        setPlayerState({
          mediaComplete: false,
          mediaStarted: true,
          adPodStarted: true,
          clickedEndSlate: false,
          playerLoading: false,
          segmentSourceName,
          segmentSourceType,
          segmentReferrer,
          isVideoOverlayPresent: false,
        });
      },
      onAdLoadStart: () => {
        console.debug('!!live - onAdLoadStart');
      },
      onAdLoadComplete: () => {
        console.debug('!!live - onAdLoadComplete');
      },
      onRemovePlayer: () => {
        setSegmentContentPersonality(false);
      },
      onExitEventStream: e => {
        this.onExitEventStream(e);
      },
      onRestartButtonClicked: () => {
        console.debug('!!live - onRestartButtonClicked');
        const currentVideo = this.getLiveVideoData();
        const playerScreenUrl = _.get(currentVideo, 'playerScreenUrl');
        if (!playerScreenUrl) return;

        playerSetFullscreen(false, playerReference);
        const hashCallSign = callSign ? `-${callSign}` : '';
        saveLiveRestart(currentVideo);
        const watchUrl = helperGetRelativeUrl({ url: playerScreenUrl });
        helperGoToRelativeUrl(`${watchUrl}#${RESTART_TYPE_LIVE}${hashCallSign}`);
      },
      onFullScreen: e => {
        const { fullscreen } = e;
        setPlayerState({ isFullscreen: fullscreen });
        this.videoProps.video_is_fullscreen = fullscreen;
        this.playbackProps.full_screen = fullscreen;
      },
      onChromecastStart: contentVideoData => {
        const castData = {
          displayName: _.get(contentVideoData, 'b.displayName', ''),
          sessionId: _.get(contentVideoData, 'b.sessionId', ''),
          status: _.get(contentVideoData, 'b.status', ''),
        };
        updateNielsenOTTStatus(true, castData);
        floodlightEvent({
          mediaInfo: this.getLiveVideoData(),
          podId: _.get(this.contentProps, 'pod_id', ''),
        });
      },
      onChromecastComplete: () => {
        updateNielsenOTTStatus(false);
        floodlightEvent({
          mediaInfo: this.getLiveVideoData(),
          podId: _.get(this.contentProps, 'pod_id', ''),
        });
      },
      onPlaybackStart: contentVideoData => {
        console.debug('!!live - leapfrog onPlaybackStart');
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        this.videoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.contentProps = _.get(contentVideoData, 'trackingData.content', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          position: 0, // position is always 0 for playback start
          video_player_content_type: null,
          content_asset_id: _.get(contentVideoData, 'trackingData.content_asset_id'),
          content_pod_ids: _.get(contentVideoData, 'trackingData.content_pod_ids'),
          content_pod_id: null,
          rating: _.get(this.videoProps, 'video_rating'),
          session_id: this.sessionId ?? '',
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackStart,
          props: data,
        });
      },
      onPlayerError: err => {
        this.setState({ playerError: true });
        const { contentStarted } = this.state;
        const { panelVideo, setPopUpModal, isAdBlockerEnabled, setError } = this.props;
        const {
          oops_message_ad_block_button_text: adBlockButtonText,
          oops_message_ad_block_detect: adBlockDetectText,
          oops_message_ad_block_failure: adBlockFailureText,
        } = window.Language;
        const eventVideoProps = _.get(panelVideo, 'trackingData.video', {});
        const { message } = err || {};
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
        };
        const videoData = {
          video_id: _.get(panelVideo, 'id'),
          video_name: _.get(panelVideo, 'name'),
          ..._.pick(data, [
            'position',
            'livestream',
            'content_pod_id',
            'video_player_state',
            'video_asset_id',
            'video_fox_profile',
            'video_content_length',
            'video_content_type',
            'video_content_length_format',
            'video_station_id',
          ]),
          type: contentStarted ? 'videoStreamInterruptedError' : 'videoStartError',
        };
        if (isAdBlockerEnabled) {
          setAdBlockDetectCookie({ value: true, options: { path: '/' } });
          setPopUpModal({ visibility: true, message: adBlockDetectText, buttonText: 'OK' });
        } else if (!isAdBlockerEnabled && getAdBlockDetectCookie()) {
          removeAdBlockDetectCookie({ options: { path: '/' } });
          setPopUpModal({
            buttonText: adBlockButtonText,
            message: adBlockFailureText,
            visibility: true,
          });
        }
        const errorObject = new Error(message);
        errorObject.type = contentStarted ? 'videoStreamInterruptedError' : 'videoStartError';
        logPlayerError(errorObject, videoData);
        if (err?.error?.entitlementIssues || err?.data?.entitlementIssues) {
          const errors = getAuthZEntitlementIssues(err);
          setError(errors);
        }
      },
      onMediaStart: () => {
        console.debug('!!live - onMediaStart');
        const currentVideo = this.getLiveVideoData();
        floodlightEvent({
          mediaInfo: currentVideo,
          podId: _.get(this.contentProps, 'pod_id', ''),
        });
        const {
          onMediaCompleteData: prevOnMediaCompleteData,
          segmentReferrer: prevSegmentReferrer,
          updateShowcaseVideo,
        } = this.props;
        const { currentVideo: prevCurrentVideo } = this.state;
        const currentVideoId = _.get(currentVideo, 'id');
        const prevVideoId = _.get(prevCurrentVideo, 'id');
        if (currentVideoId !== prevVideoId) {
          this.setState({ currentVideo });
          if (prevCurrentVideo) updateShowcaseVideo(currentVideo);
        }
        const { segmentSourceName, segmentSourceType } = this.getSegmentSourceData();
        const segmentReferrer = getSegmentReferrer({
          segmentSourceType,
          prevSegmentReferrer,
        });
        this.getUpNext(currentVideo);
        this.setState({ playerError: false });
        setPlayerState(
          {
            clickedEndSlate: false,
            firstFramePlayed: true,
            mediaComplete: false,
            mediaStarted: true,
            onMediaCompleteData: currentVideo,
            playerLoading: false,
            prevOnMediaCompleteData,
            segmentReferrer,
            segmentSourceName,
            segmentSourceType,
          },
          () => {
            const { isAutoPlay } = this.props;
            if (isAutoPlay) {
              this.trackPageReferrerAnalytics({
                name: PageNames.CONTINUOUS_PLAY,
              });
            }
          },
        );
      },
      onMediaPlaying: () => {
        // console.debug('!!live - onMediaPlaying');
      },
      onContentStart: contentVideoData => {
        console.debug('!!live - leapfrog onContentStart');
        const { completedPodId } = this.props;
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.contentProps = _.get(contentVideoData, 'trackingData.content', {});
        const data = {
          ...this.videoProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
        };
        const videoData = {
          video_id: _.get(contentVideoData, 'id'),
          video_name: _.get(contentVideoData, 'name'),
          ..._.pick(data, [
            'position',
            'livestream',
            'content_pod_id',
            'video_player_state',
            'video_asset_id',
            'video_fox_profile',
            'video_content_length',
            'video_content_type',
            'video_content_length_format',
            'video_station_id',
          ]),
        };
        this.setState({ contentStarted: true });
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoContentStart,
          props: data,
        });
        logPlayerEvent('onVideoPlaybackStart', videoData);
        if (completedPodId) setPlayerState({ completedPodId: null });
        window?.fennecInstance?.deltaContentStart(contentVideoData);
      },
      onContentPlaying: contentVideoData => {
        const { secondsViewed: previouslyViewed } = this.state;
        const secondsViewed = _.get(contentVideoData, 'trackingData.contentSecondsViewed', 0);
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        if (
          secondsViewed !== previouslyViewed &&
          secondsViewed % SEGMENT_CONTENT_PLAYING_INTERVAL_SEC === 0
        ) {
          const data = {
            ...this.videoProps,
            ...this.contentProps,
            ...eventVideoProps,
            video_seconds_viewed: SEGMENT_CONTENT_PLAYING_INTERVAL_SEC,
            session_id: this.sessionId ?? '',
          };
          callSegmentPlayerEvent({
            eventName: SegmentVideoEvents.videoContentPlaying,
            props: data,
          });
          window?.fennecInstance?.deltaContentPlaying(contentVideoData);
        }
        if (secondsViewed !== previouslyViewed) {
          const videoPlayhead = { [this.sessionId ?? '']: secondsViewed };
          if (window) _.set(window, '_segHBPlayheads', videoPlayhead);
        }
        this.setState({ secondsViewed });
      },
      onBufferStart: contentVideoData => {
        console.debug('!!live - leapfrog onBufferStart');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackBufferStarted,
          props: data,
        });
      },
      onBufferEnd: contentVideoData => {
        console.debug('!!live - leapfrog onBufferEnd');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackBufferComplete,
          props: data,
        });
      },
      onAdStart: contentVideoData => {
        console.debug('!!live - leapfrog onAdStart');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const content = {
          ..._.pick(this.contentProps, ['airdate', 'asset_id', 'full_episode', 'program', 'title']),
          ..._.pick(this.videoProps, [
            'load_type',
            'position',
            'total_length',
            'video_content_length',
            'video_nielsen_clientid',
            'video_nielsen_subbrand',
            'video_tms_id',
          ]),
        };
        const props = {
          content,
          ...this.videoProps,
          ...eventVideoProps,
          ...this.adData,
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdStarted,
          props,
        });
      },
      onAdPlaying: contentVideoData => {
        console.debug('!!live - leapfrog onAdPlaying');
        this.newAdTime = _.get(contentVideoData, 'trackingData.adSecondsViewed', 0);
        if (this.newAdTime !== this.previousAdTime && this.newAdTime % this.AD_INTERVAL_SEC === 0) {
          const eventAdProps = _.get(contentVideoData, 'trackingData.ad', {});
          const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
          const content = {
            ..._.pick(this.contentProps, [
              'airdate',
              'asset_id',
              'full_episode',
              'program',
              'title',
            ]),
            ..._.pick(this.videoProps, [
              'load_type',
              'position',
              'total_length',
              'video_content_length',
              'video_nielsen_clientid',
              'video_nielsen_subbrand',
              'video_tms_id',
            ]),
          };
          const props = {
            content,
            ...this.videoProps,
            ...this.adData,
            ...eventAdProps,
            ...eventVideoProps,
            video_seconds_viewed:
              this.newAdTime - (this.newAdTime > this.previousAdTime ? this.previousAdTime : 0),
            session_id: this.sessionId ?? '',
          };
          callSegmentPlayerEvent({
            eventName: SegmentVideoEvents.videoAdPlaying,
            props,
          });
        }
        this.previousAdTime = this.newAdTime;
      },
      onAdComplete: contentVideoData => {
        console.debug('!!live - leapfrog onAdComplete');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.newAdTime = _.get(contentVideoData, 'trackingData.adSecondsViewed', 0);
        const content = {
          ..._.pick(this.contentProps, ['airdate', 'asset_id', 'full_episode', 'program', 'title']),
          ..._.pick(this.videoProps, [
            'load_type',
            'position',
            'total_length',
            'video_content_length',
            'video_nielsen_clientid',
            'video_nielsen_subbrand',
            'video_tms_id',
          ]),
        };
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const props = {
          content,
          ...this.videoProps,
          ...this.adData,
          video_seconds_viewed:
            this.newAdTime === this.previousAdTime ? 0 : this.newAdTime - this.previousAdTime,
          session_id: this.sessionId ?? '',
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdCompleted,
          props,
        });
      },
      onAdPause: contentVideoData => {
        console.debug('!!live - leapfrog onAdPause');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const adVideoProps = _.get(contentVideoData, 'trackingData.ad', {});
        const data = {
          ...this.videoProps,
          ...eventVideoProps,
          ...adVideoProps,
          session_id: this.sessionId ?? '',
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackPaused,
          props: data,
        });
      },
      onAdResume: contentVideoData => {
        console.debug('!!live - leapfrog onAdResume');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.eventProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const data = {
          ...this.eventProps,
          ...this.adData,
          session_id: this.sessionId ?? '',
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdResumed,
          props: data,
        });
      },
      onSlateStart: contentVideoData => {
        console.debug('!!live - leapfrog onSlateStart');
        this.slateData = _.get(contentVideoData, 'trackingData.slate', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const content = {
          ..._.pick(this.contentProps, ['airdate', 'asset_id', 'full_episode', 'program', 'title']),
          ..._.pick(this.videoProps, [
            'load_type',
            'position',
            'total_length',
            'video_content_length',
            'video_nielsen_clientid',
            'video_nielsen_subbrand',
            'video_tms_id',
          ]),
        };
        const props = {
          content,
          ...this.videoProps,
          ...eventVideoProps,
          ...this.slateData,
          pod_id: 1,
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoSlateStarted,
          props,
        });
      },
      onSlatePlaying: contentVideoData => {
        console.debug('!!live - leapfrog onSlatePlaying');
        this.newSlateTime = _.get(contentVideoData, 'trackingData.slateSecondsViewed', 0);
        if (
          this.newSlateTime !== this.previousSlateTime &&
          this.newSlateTime % this.AD_INTERVAL_SEC === 0
        ) {
          const eventSlateProps = _.get(contentVideoData, 'trackingData.slate', {});
          const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
          const content = {
            ..._.pick(this.contentProps, [
              'airdate',
              'asset_id',
              'full_episode',
              'program',
              'title',
            ]),
            ..._.pick(this.videoProps, [
              'load_type',
              'position',
              'total_length',
              'video_content_length',
              'video_nielsen_clientid',
              'video_nielsen_subbrand',
              'video_tms_id',
            ]),
          };
          const props = {
            content,
            ...this.videoProps,
            ...this.slateData,
            ...eventSlateProps,
            ...eventVideoProps,
            pod_id: 1,
            video_seconds_viewed:
              this.newSlateTime -
              (this.newSlateTime > this.previousSlateTime ? this.previousSlateTime : 0),
            session_id: this.sessionId ?? '',
          };
          callSegmentPlayerEvent({
            eventName: SegmentVideoEvents.videoSlatePlaying,
            props,
          });
        }
        this.previousSlateTime = this.newSlateTime;
      },
      onSlateComplete: contentVideoData => {
        console.debug('!!live - leapfrog onSlateComplete');
        this.slateData = _.get(contentVideoData, 'trackingData.slate', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.newSlateTime = _.get(contentVideoData, 'trackingData.slateSecondsViewed', 0);
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const content = {
          ..._.pick(this.contentProps, ['airdate', 'asset_id', 'full_episode', 'program', 'title']),
          ..._.pick(this.videoProps, [
            'load_type',
            'position',
            'total_length',
            'video_content_length',
            'video_nielsen_clientid',
            'video_nielsen_subbrand',
            'video_tms_id',
          ]),
        };
        const props = {
          content,
          ...this.videoProps,
          ...this.slateData,
          pod_id: 1,
          video_seconds_viewed:
            this.newSlateTime === this.previousSlateTime
              ? 0
              : this.newSlateTime - this.previousSlateTime,
          session_id: this.sessionId ?? '',
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoSlateCompleted,
          props,
        });
      },
      onPause: contentVideoData => {
        console.debug('!!live - leapfrog onPause');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackPaused,
          props: data,
        });
      },
      onResume: contentVideoData => {
        console.debug('!!live - leapfrog onResume');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackResumed,
          props: data,
        });
      },
      onContentComplete: contentVideoData => {
        console.debug('!!live - leapfrog onContentComplete');
        const totalSecondsViewed = _.get(contentVideoData, 'trackingData.contentSecondsViewed', 0);
        const secondsViewed = Math.floor(totalSecondsViewed % SEGMENT_CONTENT_PLAYING_INTERVAL_SEC);
        const completedPodId = getSegmentPodId(contentVideoData);
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const data = {
          ...this.videoProps,
          ...this.contentProps,
          ...eventVideoProps,
          video_seconds_viewed: secondsViewed,
        };
        setPlayerState({ completedPodId });
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoContentComplete,
          props: data,
        });
        this.setState({ contentStarted: false });
      },
      onPlaybackComplete: contentVideoData => {
        console.debug('!!live - leapfrog onPlaybackComplete');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
        };

        data.video_player_content_type = null;
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackComplete,
          props: data,
        });
        setPlayerState({ completedPodId: null });
      },
      onPlaybackInterrupted: data => {
        console.debug('!!live - leapfrog onPlaybackInterrupted');
        const { method } = data;
        const props = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          method,
          video_player_state: SegmentPlayerStates.PAUSE,
          session_id: this.sessionId ?? '',
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackInterrupted,
          props,
        });
      },
      onMediaComplete: () => {
        console.debug('!!live - onMediaComplete');
        setPlayerState(
          {
            mediaComplete: true,
            mediaEnding: false,
            isAutoPlay: true,
            mediaEndingSeconds: null,
            firstFramePlayed: true,
          },
          () => {
            this.clearIntervalFetchUpNext();
            this.sendNextVideo();
          },
        );
      },
      onUserInteraction: () => {
        this.resetStillWatchingTimeout();
      },
      onNielsenDtvrTag: contentVideoData => {
        console.debug('!!live - onNielsenDtvrTag');
        const nielsen_id3_tag = _.get(contentVideoData, 'dtvrTag', {});
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoNielsenTagDetected,
          props: {
            nielsen_id3_tag,
            session_id: this.sessionId ?? '',
          },
        });
      },
      onVolumeChange: ({ newVolume, oldVolume }) => {
        const { video_volume } = this.videoProps;
        if (video_volume === newVolume || oldVolume === newVolume) return null;
        this.videoProps.video_volume = newVolume;
        this.playbackProps.sound = newVolume;
        return null;
      },
    };
  };

  getSegmentSourceData = () => {
    const video = this.getLiveVideoData();
    const { isAutoPlay: autoplay, preauthorizedResources, prevVideo } = this.props;
    const location = getLocation(window.referrer);
    const { pathname: path } = location;
    const segmentSourceType = getSegmentSourceType({
      homeUrl: helperGetHomeUrl(),
      path,
      autoplay,
      epg: true,
    });
    const data = getSegmentLiveViewedVideo({
      video,
      prevVideo,
      segmentSourceType,
      preauthorizedResources,
    });
    const segmentSourceName = getSegmentSourceName({
      data,
      type: segmentSourceType,
    });
    return {
      segmentSourceName,
      segmentSourceType,
    };
  };

  getUpNext(video) {
    const { accessToken, fetchUpNext } = this.props;
    const videoId = _.get(video, 'id');
    if (videoId) fetchUpNext({ accessToken, videoId });
  }

  setIntervalFetchUpNext(validFor) {
    const { setInterval } = this.props;
    this.clearIntervalFetchUpNext();
    this.intervalFetchUpNext = setInterval(() => {
      const currentVideo = this.getLiveVideoData();
      this.getUpNext(currentVideo);
    }, validFor);
  }

  setShowUpNextTimeout(nextVideos) {
    const { clearTimeout, playerType, setTimeout } = this.props;
    if (!UpNextToastEnabled || isPlayerTypeHero(playerType)) return;
    clearTimeout(this.showUpNextTimeout);
    const currentEndDate = _.get(nextVideos, '0.endDate', null);
    const nextStartDate = _.get(nextVideos, '1.startDate', null);
    const currentEnd = currentEndDate && stringToDate(currentEndDate);
    const nextStart = nextStartDate && stringToDate(nextStartDate);
    const hasOverlap = currentEnd && nextStart && isBefore(nextStart, currentEnd);

    if (!hasOverlap) return;

    const { toastDisplayBeforeEndInMinutes: minutesToSubtract } = window.ExternalConfig;
    const showTime = subMinutes(nextStart, minutesToSubtract);
    const showInMs = differenceInMilliseconds(showTime, now());
    if (showInMs < 0) return;
    this.showUpNextTimeout = setTimeout(() => {
      this.cbShowUpNextToast();
    }, showInMs);
  }

  callbackShow = () => {
    const {
      account,
      breakpoint,
      clearTimeout,
      firstFramePlayed,
      playerReference,
      setPlayerState,
      stillWatchingShowTimeout,
    } = this.props;
    const profileId = _.get(account, 'profileId', null);
    const { stillWatchingTimeoutEnabled } = BREAKPOINTS[breakpoint];

    clearTimeout(stillWatchingShowTimeout);
    if (stillWatchingTimeoutEnabled && firstFramePlayed) {
      playerSetFullscreen(false, playerReference);
      playerPause(playerReference);
      playerHideControls(playerReference);
      this.stillWatchingShowCb({ profileId });
      const stillWatchingHideTimeout = this.setStillWatchingHideTimeout({
        cb: () => {
          playerPlay(playerReference);
          this.stillWatchingHideCb();
          window.location = helperGetHomeUrl();
        },
      });
      setPlayerState({ stillWatchingHideTimeout });
    }
  };

  locationCallback = (error, coordinates) => {
    const { setCoordinates } = this.props;

    if (error) {
      setCoordinates(null);
      const errorCode = _.get(error, 'code');
      const errorMessage = _.get(error, 'message');
      const didTimeout = errorCode === 3 || _.includes(_.toLower(errorMessage), 'time');
      this.setState({
        geoLocationDeclined: true,
        geoLocationLoading: false,
        geoLocationTimeoutFail: didTimeout,
      });
    }

    if (coordinates) {
      setCoordinates(coordinates);
      this.setState({ geoLocationDeclined: false, geoLocationLoading: false });
    }
  };

  requestLocation = () => {
    getGeoLocation((error, coordinates) => {
      if (error) this.locationCallback(error, null);
      if (coordinates) this.locationCallback(null, coordinates);
    });
  };

  resetStillWatchingTimeout = () => {
    const {
      breakpoint,
      clearTimeout,
      firstFramePlayed,
      setPlayerState,
      stillWatchingShowTimeout,
    } = this.props;
    clearTimeout(stillWatchingShowTimeout);
    const { stillWatchingTimeoutEnabled } = BREAKPOINTS[breakpoint];

    const debugDisabledTimeout = getCookie({ name: DEBUG_PANEL_TIMEOUT_DISABLED });
    const durationSeconds = _.get(window.ExternalConfig, 'stillWatching.liveLimitSeconds');
    if (stillWatchingTimeoutEnabled && firstFramePlayed && debugDisabledTimeout !== 'YES') {
      const duration = durationSeconds && durationSeconds * 1000;
      const newStillWatchingShowTimeout = this.setStillWatchingShowTimeout({
        cb: this.callbackShow,
        duration,
      });
      setPlayerState({ stillWatchingShowTimeout: newStillWatchingShowTimeout });
    }
  };

  cbShowUpNextToast() {
    const { clearTimeout, setTimeout } = this.props;
    const { toastDisplayDurationInSeconds } = window.ExternalConfig;
    this.setState({ showUpNextToast: true });

    clearTimeout(this.hideUpNextTimeout);
    this.hideUpNextTimeout = setTimeout(() => {
      this.setState({ showUpNextToast: false });
    }, toastDisplayDurationInSeconds * 1000);
  }

  clearIntervalFetchUpNext() {
    const { clearInterval, clearNextVideosValidFor } = this.props;
    clearInterval(this.intervalFetchUpNext);
    clearNextVideosValidFor();
  }

  sendNextVideo() {
    const { playerType, setVideoPanel } = this.props;
    if (!UpNextStreamOverrideEnabled || isPlayerTypeHero(playerType)) return;
    const { nextVideos } = this.props;
    const currentVideo = _.get(nextVideos, '0');
    const nextVideo = _.get(nextVideos, '1');
    if (!currentVideo || !nextVideo) return;
    const network = _.get(nextVideo, 'network');
    const hasSportsContent = isSportsContent(currentVideo) || isSportsContent(nextVideo);
    if (network !== FOX || !hasSportsContent) return;
    setVideoPanel({ video: nextVideo, source: PLAYER_CONTEXT_LIVE });
  }

  renderVideoDetail(opts = {}) {
    const {
      adPlaying,
      breakpoint,
      creditsPlaying,
      favoritesManager,
      isContextPlayer,
      isFullscreen,
      panelVideo,
      playerType,
      setPlayerDataHover,
      setStickyControls,
      theme,
      video,
    } = this.props;
    const { currentVideo } = this.state;
    const { renderInfoInDetail, fncHideVideoDetails } = BREAKPOINTS[breakpoint];
    const { showControls, isFNC } = opts;
    const outOfMarketLivePlayerData = getCookie({ name: OOM_LIVE_PLAYER_URL_COOKIE_NAME });
    const classes = classNames(
      isFullscreen ? theme.fullscreen : false,
      theme.watchDetailContainer,
      isEpgv2Enabled() && theme.epgLivePageWatchDetailContainer,
    );
    if (isPlayerTypeHero(playerType)) return null;
    const videoItem = getOomLivePlayerEnabled() ? panelVideo : currentVideo || panelVideo;

    const renderVideoDetail = () => (
      <div className={classes}>
        <VideoDetail
          {...getVideoDetailProps(videoItem)}
          adPlaying={adPlaying}
          favoritesManager={favoritesManager}
          isContextPlayer={isContextPlayer}
          isLive
          renderInfo={renderInfoInDetail}
          setPlayerDataHover={setPlayerDataHover}
          setStickyControls={setStickyControls}
          showControls={showControls}
          showNetworkIcon={!creditsPlaying}
          video={video}
        />
      </div>
    );

    if (outOfMarketLivePlayerData) return currentVideo && renderVideoDetail();
    return isFNC && fncHideVideoDetails ? null : renderVideoDetail();
  }

  render() {
    const {
      adPlaying,
      breakpoint,
      coordinates,
      fetchedVideo,
      nextVideos,
      panelVideo,
      playerDataHover,
      playerLoading,
      playerType,
      renderVideoContainer,
      setLoading,
      showControls,
      theme,
      video,
      isFullscreen,
      isMiniPlayerActive,
      handlePopInMiniPlayer,
      containerId,
    } = this.props;
    const {
      geoLocationDeclined,
      geoLocationLoading,
      geoLocationTimeoutFail,
      showStillWatching,
      showUpNextToast,
      playerError,
    } = this.state;

    if (isMiniPlayerActive && playerError) handlePopInMiniPlayer();

    const callSign = _.get(fetchedVideo, 'callSign');
    const guid = _.get(fetchedVideo, 'guid');
    const livePlayerScreenUrl = _.get(fetchedVideo, 'livePlayerScreenUrl');
    const { showMobileControls, upNextToastHidesVideoDetail } = BREAKPOINTS[breakpoint];
    if (isPlayerTypeHero(playerType) && geolocationEnabled() && !coordinates) return null;
    if (geoLocationLoading === true) return <Loader setLoading={setLoading} center />;
    if (geoLocationDeclined === null) return <div className={theme.playerSpacer} />;
    const outOfMarketPlayerImage = getCookie({ name: OOM_LIVE_PLAYER_IMAGE_COOKIE_NAME });
    const isWatchUrl = livePlayerScreenUrl?.indexOf('/v2.0/watch') >= 0;
    let liveContentId = _.last(_.split(livePlayerScreenUrl, '/'));
    if (isWatchUrl) {
      const { stationId, epgListingId } = getUrlParams(livePlayerScreenUrl);
      liveContentId = `${stationId}?epgListingId=${epgListingId}`;
    }

    let seriesBackground = _.get(
      panelVideo,
      'images.videoList.raw',
      _.get(panelVideo, 'images.still.raw', _.get(panelVideo, 'images.seriesList.raw')),
    );
    if (!seriesBackground && outOfMarketPlayerImage) {
      seriesBackground = outOfMarketPlayerImage;
    }

    const background = getDynamicImage({
      breakpoint,
      context: LIVE_PLAYER_BACKGROUND_IMAGE,
      seriesBackground,
    });
    const currentVideo = this.getLiveVideoData();
    const backfade =
      ((showControls || playerDataHover || playerLoading) && !adPlaying) || showUpNextToast;
    const showVideoDetail =
      (showControls ||
        (playerDataHover && !isFullscreen) ||
        (showMobileControls && !isFullscreen)) &&
      currentVideo &&
      !playerLoading;

    const contextProps = {
      backfade,
      background,
      callSign,
      channel: callSign,
      containerId,
      geoLocationDeclined: geolocationEnabled() && geoLocationDeclined && !coordinates,
      geoLocationTimeoutFail,
      geoLocationPrimaryActionCallback: geolocationEnabled() ? this.requestLocation : null,
      guid,
      isFullscreen,
      isLive: true,
      liveContentId,
      mediaInfo: video,
      mode: LIVE,
      onStillWatchingClose: this.onStillWatchingClose,
      playerConfig: this.getPlayerEvents(),
      playerLoading,
      playerLoadingImage: background,
      showControls: showVideoDetail,
      showStillWatching,
      showUpNextToast,
      type: playerType,
    };

    const currentPlayerState = getPlayerState(getActivePlayer());
    if (!this.currentChannel) this.currentChannel = callSign;
    if (
      this.currentChannel &&
      this.currentChannel !== callSign &&
      (currentPlayerState === 'PLAYING' || currentPlayerState === 'AD')
    ) {
      this.currentChannel = callSign;
      callSegmentPlayerEvent({
        eventName: SegmentVideoEvents.videoPlaybackInterrupted,
        props: {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          method: INTERRUPTION_METHODS.BROWSER_ACTION,
          video_player_state: SegmentPlayerStates.PAUSE,
          session_id: this.sessionId ?? '',
        },
      });
    }

    const nextVideo = _.get(nextVideos, '1', null);
    const hideVideoDetail = showUpNextToast && upNextToastHidesVideoDetail;
    const renderVideoDetail = showVideoDetail && !hideVideoDetail;
    return (
      <>
        {renderVideoContainer({ props: contextProps })}
        {this.renderVideoDetail({ showControls: renderVideoDetail })}
        {showUpNextToast && nextVideo && !isPlayerTypeHero(playerType) && (
          <UpNextToast
            onWatch={this.onWatchUpNextToast}
            onHide={this.onHideUpNextToast}
            renderImage={image =>
              renderImage({
                breakpoint,
                imageType: LIVE_EPG_THUMBNAIL_IMAGE,
                ...image,
              })
            }
            video={nextVideo}
          />
        )}
      </>
    );
  }
}

LiveContext.defaultProps = {
  accessToken: null,
  account: null,
  adPlaying: null,
  anonymousAccount: {},
  anonymousId: null,
  anonymousLogin: noop,
  clickedThumbnail: null,
  completedPodId: null,
  containerId: null,
  continuous: null,
  continuousPlayback: null,
  coordinates: null,
  creditsPlaying: null,
  errors: null,
  favoritesManager: null,
  fetchedVideo: null,
  firstAdPlayed: null,
  firstFramePlayed: null,
  isAdBlockerEnabled: false,
  isAuthenticated: null,
  isAutoPlay: null,
  isContextPlayer: false,
  isFullscreen: null,
  location: null,
  mediaComplete: null,
  mediaEnding: null,
  mediaEndingSeconds: null,
  nextVideosValidFor: null,
  onMediaCompleteData: null,
  panelVideo: null,
  playerLoading: null,
  playlist: null,
  preauthorizedResources: null,
  prevOnMediaCompleteData: null,
  prevVideo: null,
  provider: null,
  recommendationIdData: null,
  restart: null,
  restartPlayerScreenUrl: null,
  restartVideo: null,
  saveLiveRestart: noop,
  segmentReferrer: null,
  segmentSourceName: null,
  segmentSourceType: null,
  setPopUpModal: noop,
  setLoading: noop,
  setRecommendationId: noop,
  shouldResetStillWatching: null,
  showEndCards: null,
  startedPlayback: null,
  stillWatchingHideTimeout: noop,
  stillWatchingShowTimeout: null,
  video: null,
};

LiveContext.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  adPlaying: PropTypes.bool,
  anonymousAccount: PropTypes.shape({}),
  anonymousId: PropTypes.string,
  anonymousLogin: PropTypes.func,
  breakpoint: PropTypes.string.isRequired,
  clearInterval: PropTypes.func.isRequired,
  clearNextVideosValidFor: PropTypes.func.isRequired,
  clearTimeout: PropTypes.func.isRequired,
  clickedThumbnail: PropTypes.bool,
  completedPodId: PropTypes.string,
  containerId: PropTypes.string,
  continuous: PropTypes.bool,
  continuousPlayback: PropTypes.bool,
  coordinates: PropTypes.shape({}),
  creditsPlaying: PropTypes.bool,
  defaultPlayerEvents: PropTypes.shape({}).isRequired,
  errors: PropTypes.number,
  favoritesManager: PropTypes.shape({}),
  fetchUpNext: PropTypes.func.isRequired,
  fetchedVideo: PropTypes.shape({}),
  firstAdPlayed: PropTypes.bool,
  firstFramePlayed: PropTypes.bool,
  floodlightEvent: PropTypes.func.isRequired,
  getUseUtagGlobal: PropTypes.func.isRequired,
  handlePopInMiniPlayer: PropTypes.func,
  isMiniPlayerActive: PropTypes.bool,
  isAdBlockerEnabled: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  isAutoPlay: PropTypes.bool,
  isContextPlayer: PropTypes.bool,
  isFullscreen: PropTypes.bool,
  location: PropTypes.shape({}),
  mediaComplete: PropTypes.bool,
  mediaEnding: PropTypes.bool,
  mediaEndingSeconds: PropTypes.number,
  nextVideos: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  nextVideosValidFor: PropTypes.number,
  onMediaCompleteData: PropTypes.shape({}),
  openSignIn: PropTypes.func.isRequired,
  openSignUp: PropTypes.func.isRequired,
  pageViewEvent: PropTypes.func.isRequired,
  panelVideo: PropTypes.shape({}),
  playerDataHover: PropTypes.bool.isRequired,
  playerLoading: PropTypes.bool,
  playerReference: PropTypes.string,
  playerType: PropTypes.string.isRequired,
  playlist: PropTypes.bool,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  prevOnMediaCompleteData: PropTypes.shape({}),
  prevVideo: PropTypes.shape({}),
  provider: PropTypes.shape({}),
  recommendationIdData: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})]),
  renderVideoContainer: PropTypes.func.isRequired,
  restart: PropTypes.bool,
  restartPlayerScreenUrl: PropTypes.string,
  restartVideo: PropTypes.shape({}),
  saveLiveRestart: PropTypes.func,
  segmentReferrer: PropTypes.shape({}),
  segmentSourceName: PropTypes.string,
  segmentSourceType: PropTypes.string,
  setCoordinates: PropTypes.func.isRequired,
  setError: PropTypes.func,
  setInterval: PropTypes.func.isRequired,
  setLoading: PropTypes.func,
  setPlayerDataHover: PropTypes.func.isRequired,
  setPlayerState: PropTypes.func.isRequired,
  setPopUpModal: PropTypes.func,
  setRecommendationId: PropTypes.func,
  setShouldResetStillWatching: PropTypes.func,
  setStickyControls: PropTypes.func.isRequired,
  setStreamExit: PropTypes.func.isRequired,
  setTimeout: PropTypes.func.isRequired,
  setVideoPanel: PropTypes.func.isRequired,
  setVideoStartedPlayback: PropTypes.func.isRequired,
  shouldResetStillWatching: PropTypes.bool,
  showControls: PropTypes.bool.isRequired,
  showEndCards: PropTypes.bool,
  startedPlayback: PropTypes.bool,
  stillWatchingHideTimeout: PropTypes.func,
  stillWatchingShowTimeout: PropTypes.number,
  theme: PropTypes.shape({}).isRequired,
  updateShowcaseVideo: PropTypes.func,
  video: PropTypes.shape({}),
};

export default ReactTimeout(LiveContext);
